import React, { useEffect, useState } from "react";
import Cookies from "js-cookie";
import { Button, Flex } from "@chakra-ui/react";
import { type Banner } from "lib/sanity";
import { trackEvent, trackMutinyConversion } from "lib/analytics";
import { useColorTheme } from "contexts/color-theme";
import { textStyles } from "theme/text-styles";
import { LegacyCloseIcon } from "ui/icons";
import { Link } from "ui/link";
import { Section } from "./section";

export function AnnouncementBanner({ title, description, ctaLink, ctaNewTab }: Banner) {
  const [isShow, setIsShow] = useState(false);
  const cookieTitle = title.toLowerCase().replace(" ", "-") + "--hidden";
  const hideBannerCookie = Cookies.get(cookieTitle);
  const { theme, bgColor } = useColorTheme();

  const handleClose = () => {
    trackEvent("Announcement Banner Closed", { title, page: window.location.href });
    trackMutinyConversion("announcement-banner-closed");
    setIsShow(false);
    Cookies.set(cookieTitle, "true", { expires: 1 });
  };

  useEffect(() => {
    if (hideBannerCookie !== "true") {
      setIsShow(true);
    }
  }, [hideBannerCookie]);

  // Default banner colors - will be shown on pages with plain white heros
  let bannerBg = "new.green.50";
  let textColor = "new.green.900";

  // Any of the colorful heros will get a themed banner (with 900-shade text and either a white or 50-shade bg)
  if (bgColor.includes(".")) {
    const variant = bgColor.split(".")[1];

    bannerBg = theme === "light" ? "white" : `new.${variant}.50`;
    textColor = `new.${variant}.900`;
  }

  return (
    <Section
      role="alert"
      display={isShow ? "flex" : "none"}
      bg={bannerBg}
      color="brand.text.light"
      py={{ base: "tiny", md: "smallest" }}
      pos="relative"
      w="100%"
      zIndex="modal"
    >
      <Flex w={{ base: "80vw", md: "100%" }} justify={{ base: "start", md: "center" }}>
        <Link
          href={ctaLink}
          target={ctaNewTab ? "_blank" : "_self"}
          {...textStyles.caption1}
          color={textColor}
          _hover={{
            textUnderlineOffset: "3px",
            textDecoration: "underline",
          }}
          onClick={() => {
            trackEvent("Announcement Banner Clicked", { title, page: window.location.href });
            trackMutinyConversion("announcement-banner-clicked");
          }}
        >
          {description}
        </Link>
      </Flex>

      <Button
        aria-label="Close alert"
        variant="unstyled"
        pos="absolute"
        right="tiny"
        top={{ base: "tiny", md: 0 }}
        height="100%"
        display="flex"
        alignItems={{ base: "start", md: "center" }}
        justifyContent="center"
      >
        <LegacyCloseIcon
          color={textColor}
          boxSize="icon.md"
          onClick={() => {
            handleClose();
          }}
        />
      </Button>
    </Section>
  );
}
